import React from 'react';
import styled, { css } from 'styled-components';
import { flatten, sumBy, map, filter, get, inRange, includes } from 'lodash';
import { FlexContainer, Text, Row, Column } from '../../common';
import moment from 'moment';
import { array, bool, func } from 'prop-types';

const VerticalSeparator = styled.div`
    border-left: 1px solid #eaeaea;
    width: 1px;
    display: inline-block;
    margin-left: 15px;
    margin-right: 15px;
`;

const SpaceBetween = styled.div`
    width: 8px;
`;

const CounterBox = styled.div`
    text-align: center;
    background-color: ${({ backgroundColor = '#f4f4f4' }) => backgroundColor};
    ${({ marginRight }) => marginRight && css`margin-right: ${marginRight}px;`}
    border-radius: 11.5px;
    width: 44px;
    font-size: 15px;
    line-height: 21px;
`;

const Header = ({ store, isMobile, showResumeMobile, setShowResumeMobile }) => {
    const spaceBetweenWidth = '31px';
    const data = flatten(map(store, 'data'));
    const dateNow = moment().format('YYYY-MM-DD HH:mm');
    const windowsTimeout = filter(store, item => dateNow > get(item, 'info.end'));
    const windowsTimeleft = filter(store, item => {
        const diffTime = moment(get(item, 'info.end')).diff(moment(dateNow), 'minutes');
        return inRange(diffTime, 1, 31);
    });
  
    const countByStatus = (status) =>
        sumBy(filter(data, { status }), 'count');

    // Solo los 4 grupos de las cards. atrasado/personalShopper/fueraDePlazo son recortes, no pedidos extra.
    const isOrderCounter = ({ status }) => includes(['pendiente', 'inPicking', 'facturado', 'anulado'], status);

    const countBySla = (selectedSla) => {
        const filtered = map(filter(store, { info: { selectedSla } }), 'data');
        return sumBy(filter(flatten(filtered), isOrderCounter), 'count');
    };

    const countTotal = () => sumBy(filter(data, isOrderCounter), 'count');

    const dataWindowsTimeout = map(flatten(windowsTimeout), 'data');
    const dataWindowsTimeLimit = map(flatten(windowsTimeleft), 'data');
    const countTimeLimit = sumBy(filter(flatten(dataWindowsTimeLimit), { status: 'pendiente' }), 'count');
    const countTimeOut = sumBy(filter(flatten(dataWindowsTimeout), { status: 'pendiente' }), 'count');

    const pendingCounters = () => {
        const counters = [
            {
                value: {
                    text: `${countByStatus('pendiente')}`
                },
                container: {
                    marginRight: 3
                }
            },
            {
                value: {
                    text: `${countTimeOut}`,
                    color: '#ffffff'
                },
                container: {
                    marginRight: 3,
                    backgroundColor: '#CC444B'
                }
            },
            {
                value: {
                    text: `${countTimeLimit}`,
                    color: '#ffffff'
                },
                container: {
                    backgroundColor: '#EB963C'
                }
            }
        ];
        return map(counters, ({ value, container }) => (
            <CounterBox {...container} >
                <Text {...value} strong />
            </CounterBox>
        ));
    };

    const countersByStatus = () => {
        const counters = [
            {
                label: 'En Picking',
                value: countByStatus('inPicking')
            },
            {
                label: 'Pickeados',
                value: countByStatus('facturado')
            },
            {
                label: 'Anulados',
                value: countByStatus('anulado')
            },
            {
                label: 'Despacho',
                value: countBySla('Despacho')
            },
            {
                label: 'Retiro',
                value: countBySla('Retiro en Tienda')
            },
            {
                label: 'Click al Auto',
                value: countBySla('Click al Auto')
            }
        ];
        return map(counters, ({ label, value }) => {
            if (isMobile) {
                return (<Column align='center' minWidth={99} pY={5}>
                    <Text text={label} size={12} style={{ margin: 'auto' }} />
                    <CounterBox>
                        <Text text={`${value}`} strong />
                    </CounterBox>
                </Column>);
            }
            return (
                <>
                    <VerticalSeparator />
                    <Text text={label} size={12} style={{ margin: 'auto' }} />
                    <SpaceBetween width={spaceBetweenWidth} />
                    <CounterBox>
                        <Text text={`${value}`} strong />
                    </CounterBox>
                </>
            );
        });
    };

    const totalCounter = () => (
        <CounterBox backgroundColor='#1CA2B8'>
            <Text text={`${countTotal()}`} strong color={'#fff'} />
        </CounterBox>
    );

    const desktop = () => (
        <Row
            border='1px solid #eaeaea'
            borderRadius={3}
            padding='10px'
            style={{ backgroundColor: 'white' }}
        >
            <FlexContainer style={{ margin: 'auto' }}>
                <Text text={'Pendientes'} strong size={12} style={{ margin: 'auto' }} />
                <SpaceBetween width={spaceBetweenWidth} />
                { pendingCounters() }
                { countersByStatus() }
                <VerticalSeparator />
                <Text text={'Total'} size={12} strong style={{ margin: 'auto' }} />
                <SpaceBetween width={spaceBetweenWidth} />
                { totalCounter() }
            </FlexContainer>
        </Row>
    );

    const mobile = () => (
        <FlexContainer flex={1} direction='column'>
            <Row>
                <Column>
                    <Text text='Resumen tienda' semibold size={16} />
                </Column>
                <Column align='flex-end'>
                    <span
                        className={`icon-ic-${showResumeMobile ? 'minus' : 'plus'}`}
                        style={{
                            color: showResumeMobile ? '#848484' : '#4cb358',
                            cursor: 'pointer'
                        }}
                        onClick={() => setShowResumeMobile(!showResumeMobile)}
                    />
                </Column>
            </Row>
            <FlexContainer border='1px solid #eaeaea' borderRadius={4} direction='column' pX={15}>
                <Row style={{ borderBottom: '1px solid #eaeaea' }} pY={15}>
                    <Column>
                        <Text text='Pendientes' semibold size={14} />
                    </Column>
                    <Column direction='row' justify='flex-end'>
                        { pendingCounters() }
                    </Column>
                </Row>
                { showResumeMobile && (
                    <Row style={{ borderBottom: '1px solid #eaeaea' }}>
                        { countersByStatus() }
                    </Row>
                ) }
                <Row pY={15}>
                    <Column>
                        <Text text='Total' semibold size={14} />
                    </Column>
                    <Column direction='row' justify='flex-end'>
                        { totalCounter() }
                    </Column>
                </Row>
            </FlexContainer>
        </FlexContainer>
    );

    return (<FlexContainer pR={23} pL={23} pB={5}>
        { isMobile ? mobile() : desktop() }
    </FlexContainer>);
};

Header.propTypes = {
    stores: array,
    isMobile: bool,
    showResumeMobile: bool,
    setShowResumeMobile: func
};

export default Header;